import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { GoogleLogin } from '@react-oauth/google';
import {
  useGetAuthConfigQuery,
  useGetLegalDocumentsQuery,
  useLoginMutation,
  useRegisterMutation,
  useGoogleCallbackMutation,
  useVerifyEmailOtpMutation,
  useResendEmailOtpMutation,
  type TokenData,
} from '@/redux/internal-services/onboardingApi';
import { setSession } from '@/redux/slices/sessionStateSlice';
import type { RootState } from '@/redux/common/store';
import { useOnboardingRoute } from '@/hooks';
import AuthMarketingPanel from '@/components/Auth/AuthMarketingPanel';
import OtpCodeInput from '@/components/UI/OtpCodeInput';
import BrandLogo from '@/components/UI/BrandLogo';
import { Button } from '@/components/UI/Button';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';
import { useIsDark } from '@/hooks/useIsDark';

const EyeIcon = ({ off }: { off?: boolean }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
    {off ? (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
        <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isDark = useIsDark();
  const isAuthenticated = useSelector((state: RootState) => state.sessionState.isAuthenticated);
  const tenantId = useSelector((state: RootState) => state.sessionState.tenantId);
  const roles = useSelector((state: RootState) => state.sessionState.roles);
  const isPlatformAdmin = roles.includes('platform_admin') || roles.includes('platform_support');

  const from = (location.state as { from?: Location })?.from?.pathname ?? '/chat';

  const [isProvisioning, setIsProvisioning] = useState(false);
  // Synchronous mirror of isProvisioning. _dispatchSession updates the redux session (an external
  // store), which forces a synchronous re-render via useSyncExternalStore BEFORE the pending
  // setIsProvisioning(true) state update commits — so the redirect effect could read a stale
  // isProvisioning=false and bounce a mid-provision Google user to /register. A ref is set
  // synchronously and read correctly by the effect regardless of state-batching timing.
  const provisioningRef = useRef(false);
  const { target: onboardingTarget, isLoading: onboardingLoading } = useOnboardingRoute();

  React.useEffect(() => {
    if (!isAuthenticated) return;
    // Platform-level users (no tenant) go directly to the platform admin area.
    if (!tenantId && isPlatformAdmin) {
      navigate('/platform/tenants', { replace: true });
      return;
    }
    // While we are provisioning a workspace for a just-verified user, the session is
    // briefly authenticated with no tenant — don't bounce them to /register mid-flight.
    if (isProvisioning || provisioningRef.current) return;
    if (!tenantId) {
      navigate('/register', { replace: true });
      return;
    }
    // Onboarding routing (pre-plan setup → plans → setup → settings) is owned by
    // useOnboardingRoute. Defer until it resolves so a just-verified user isn't sent straight
    // to the dashboard, then route to its target or fall through to their intended page.
    if (onboardingLoading) return;
    if (onboardingTarget) {
      navigate(onboardingTarget, { replace: true });
    } else {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, tenantId, isPlatformAdmin, navigate, from, isProvisioning, onboardingTarget, onboardingLoading]);

  const { data: authConfigData } = useGetAuthConfigQuery();
  const { data: legalDocsData } = useGetLegalDocumentsQuery();
  const [loginMutation, { isLoading }] = useLoginMutation();
  const [registerMutation, { isLoading: isRegistering }] = useRegisterMutation();
  const [googleCallback] = useGoogleCallbackMutation();
  const [verifyEmailOtp, { isLoading: isVerifyingOtp }] = useVerifyEmailOtpMutation();
  const [resendEmailOtp, { isLoading: isResending }] = useResendEmailOtpMutation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState('');

  const [step, setStep] = useState<'login' | 'otp' | 'google_not_found'>('login');
  const [googleEmail, setGoogleEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendBlocked, setResendBlocked] = useState(false);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  useEffect(() => {
    return () => { if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, []);

  const enabledMethods = authConfigData?.data?.methods ?? ['google'];
  const showNative = enabledMethods.includes('native');
  const showGoogle = enabledMethods.includes('google');

  // GIS renders a fixed-width iframe, so a hardcoded width overflows on narrow screens. Measure the
  // wrapper and pass a pixel width capped at GIS's 400px max so the button always fits its column.
  const googleWrapRef = useRef<HTMLDivElement>(null);
  const [googleWidth, setGoogleWidth] = useState(360);
  useEffect(() => {
    const el = googleWrapRef.current;
    if (!el) return;
    const update = () => setGoogleWidth(Math.min(400, Math.max(200, Math.floor(el.clientWidth))));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [showGoogle]);
  const verificationMethod = authConfigData?.data?.verification_method ?? 'otp';
  // "link" sends an email link only (no code to type); "otp"/"both" expose a 6-digit code.
  const showOtpInput = verificationMethod === 'otp' || verificationMethod === 'both';

  const _dispatchSession = (data: TokenData) => {
    dispatch(setSession({
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      user: {
        user_id: data.user_id,
        email: data.email,
        name: data.name ?? data.email.split('@')[0],
        roles: data.roles,
        role_names: data.role_names,
        permissions: data.permissions,
        tenant_id: data.tenant_id ?? '',
        subscription_status: data.subscription_status,
        onboarding_completed: data.onboarding_completed,
        sam_welcome_shown: data.sam_welcome_shown,
      },
    }));
  };

  // Completes sign-in. A user who signed up but never finished onboarding has no
  // workspace yet (the tenant is created by register(), not signup), so login returns
  // tenant_id=null. In that case create the workspace here and flag setup as pending so
  // the redirect lands on the guided onboarding flow instead of the register page.
  const completeSession = async (data: TokenData) => {
    if (data.tenant_id) {
      _dispatchSession(data);
      return;
    }
    // Platform-level users have no tenant and must not be provisioned as a workspace.
    const isPlatformUser = data.roles?.includes('platform_admin') || data.roles?.includes('platform_support');
    if (isPlatformUser) {
      _dispatchSession(data);
      return;
    }
    provisioningRef.current = true;
    setIsProvisioning(true);
    try {
      _dispatchSession(data);
      const legalDocs = legalDocsData?.data ?? [];
      const result = await registerMutation({
        company_name: '',
        primary_contact: { name: data.name || data.email.split('@')[0], email: data.email },
        legal_acceptances: legalDocs.map((doc) => ({ document_key: doc.document_key, version: doc.version })),
        confirm_duplicate: false,
      }).unwrap();
      _dispatchSession(result.data as unknown as TokenData);
    } finally {
      provisioningRef.current = false;
      setIsProvisioning(false);
    }
  };

  const handleNativeLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      const result = await loginMutation({ email, password }).unwrap();
      await completeSession(result.data);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message_code?: number } };
      const code = apiErr?.data?.message_code;
      // Email not verified — move the user into the verification step and resend a code.
      if (code === 1108) {
        setStep('otp');
        setOtpError('');
        setResendBlocked(false);
        await sendOtp();
        return;
      }
      // Suspended from tenant — dedicated page.
      if (code === 1121) {
        navigate('/suspended/tenant');
        return;
      }
      // Account suspended at platform level (1120) or deactivated (1007).
      if (code === 1120 || code === 1007) {
        navigate('/suspended/account');
        return;
      }
      // Social-only account — no password set.
      if (code === 1118) {
        setError(t('messages.1118'));
        return;
      }
      setError(t('auth.loginFailed', 'Invalid credentials.'));
      setPassword("");
      setTimeout(() => passwordRef.current?.focus(), 0);
    }
  };

  const sendOtp = async () => {
    try {
      await resendEmailOtp({ email }).unwrap();
      setResendCooldown(60);
    } catch (err: unknown) {
      const apiErr = err as { data?: { message_code?: number } };
      if (apiErr?.data?.message_code === 3306) {
        setResendBlocked(true);
        setOtpError(t('messages.3306'));
      } else {
        setOtpError(t('auth.otpSendFailed', 'Failed to send verification code.'));
        setResendCooldown(60);
      }
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setOtpError('');
    try {
      await verifyEmailOtp({ email, otp: otpCode }).unwrap();
    } catch {
      setOtpError(t('auth.otpInvalid', 'Invalid or expired code. Please try again.'));
      return;
    }
    // Email now verified — complete the original sign-in. The post-login redirect reads
    // the tenant checklist and routes to the guided setup wizard if any required step
    // is still outstanding.
    try {
      const result = await loginMutation({ email, password }).unwrap();
      await completeSession(result.data);
    } catch {
      setOtpError(t('auth.loginFailed', 'Invalid credentials.'));
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setError('');
    let emailFromToken = '';
    try {
      emailFromToken = JSON.parse(atob(credential.split('.')[1])).email ?? '';
    } catch { /* non-critical */ }
    try {
      const result = await googleCallback({ id_token: credential }).unwrap();
      if (!result.data?.tenant_id) {
        setGoogleEmail(emailFromToken);
        setStep('google_not_found');
        return;
      }
      await completeSession(result.data);
    } catch {
      setError(t('auth.googleFailed', 'Google sign-in failed. Please try again.'));
    }
  };

  return (
    <div className="auth-bg flex min-h-dvh relative overflow-hidden">
      <AuthMarketingPanel
        heading="Run your business"
        headingAccent="with your voice."
        subtext="SAM manages your proposals, jobs, clients, and schedule — so you can focus on what matters."
        bullets={[
          'Pick up right where you left off',
          'Access your workspace from office or field',
          'Stay on top of tasks, reminders, and follow-ups',
          'Review project updates and team activity',
          'Continue conversations with SAM across your workflow',
          'Secure access to your contractor workspace',
        ]}
      />

      <div className="flex-1 flex items-center justify-center px-4 py-8 sm:px-6 sm:py-12 min-w-0">
        <div className="w-full max-w-md min-w-0 xl:max-w-[30rem]">

          {/* Mobile branding — hidden on lg+ where AuthMarketingPanel shows */}
          <div className="flex flex-col items-center mb-8 lg:hidden">
            <BrandLogo surface="auto" height={36} />
            <span className="mt-3 text-caption text-ui-text2">Run your business with your voice.</span>
          </div>

          {step === 'google_not_found' ? (
            <div className="card-surface px-5 py-8 text-center sm:px-10 sm:py-10">
              <div className="w-16 h-16 bg-danger/15 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-danger-text" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <h2 className="text-h3 text-ui-text mb-2">This account doesn't have access</h2>
              {googleEmail ? (
                <p className="text-ui-text2 text-sm mb-6">
                  The Google account <span className="font-semibold text-ui-text">{googleEmail}</span> is not associated with an active RoofVox account.
                </p>
              ) : (
                <p className="text-ui-text2 text-sm mb-6">
                  This Google account is not associated with an active RoofVox account.
                </p>
              )}
              <Button
                type="button"
                size="lg"
                onClick={() => { setStep('login'); setGoogleEmail(''); setError(''); }}
                className="mb-3 w-full"
              >
                Sign in with another account
              </Button>
              <Button type="button" variant="secondary" size="lg" onClick={() => navigate('/register')} className="w-full">
                Register my company
              </Button>
            </div>
          ) : step === 'otp' ? (
            <div className="card-surface px-5 py-8 text-center sm:px-10 sm:py-10">
              <div className="w-16 h-16 bg-accent-soft rounded-full flex items-center justify-center mx-auto mb-6">
                <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8 text-accent-text" stroke="currentColor" strokeWidth={2} aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>

              <h2 className="text-h2 text-ui-text mb-2">Verify Your Email</h2>
              <p className="text-ui-text2 text-sm mb-1">
                {showOtpInput ? 'We sent a verification code to:' : 'We sent a verification code to:'}
              </p>
              <p className="font-semibold text-ui-text text-sm mb-6">{email}</p>

              {showOtpInput ? (
                <form onSubmit={handleVerifyOtp}>
                  <p className="text-sm font-medium text-ui-text text-left mb-3">Enter 6-Digit Code</p>
                  <div className="mb-6">
                    <OtpCodeInput value={otpCode} onChange={setOtpCode} autoFocus />
                  </div>

                  {otpError && <p role="alert" className="text-danger-text text-sm mb-4">{otpError}</p>}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-full"
                    disabled={otpCode.length !== 6}
                    loading={isVerifyingOtp || isLoading || isRegistering}
                  >
                    {(isVerifyingOtp || isLoading || isRegistering) ? t('common.loading') : 'Verify & Continue'}
                  </Button>
                </form>
              ) : (
                <>
                  <p className="text-sm text-ui-text2 mb-6">
                    Click the link in that email to verify your address, then sign in again.
                  </p>
                  {otpError && <p role="alert" className="text-danger-text text-sm mb-4">{otpError}</p>}
                  <Button
                    type="button"
                    size="lg"
                    className="w-full"
                    onClick={() => { setStep('login'); setError(''); setOtpError(''); }}
                  >
                    Back to Sign In
                  </Button>
                </>
              )}

              <p className="mt-5 text-sm text-ui-text2">
                {showOtpInput ? "Didn't receive the code? " : "Didn't receive the email? "}
                {resendBlocked ? (
                  <span className="text-danger-text font-medium">{t('messages.3306')}</span>
                ) : (
                  <button
                    type="button"
                    onClick={sendOtp}
                    disabled={isResending || resendCooldown > 0}
                    className="text-accent-text font-medium hover:underline disabled:opacity-50"
                  >
                    {isResending ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend'}
                  </button>
                )}
              </p>

              {showOtpInput && (
                <button
                  type="button"
                  onClick={() => { setStep('login'); setError(''); setOtpError(''); setOtpCode(''); }}
                  className="mt-4 text-sm text-ui-text2 hover:underline"
                >
                  Back to sign in
                </button>
              )}
            </div>
          ) : (
          <>
          <h1 className="mb-1.5 text-center text-[1.25rem] font-bold text-ui-text sm:text-h2 xl:text-[1.75rem]">
            Sign in to your workspace
          </h1>
          <p className="mb-5 text-balance text-center text-xs text-ui-text2 sm:text-sm">
            Good to see you! Sign in to pick up where you left off.
          </p>

          {showGoogle && (
            // GIS renders a fixed-width iframe; center it and give it a min-height so the Google
            // button row matches the height of the native email/password inputs (~44px).
            // [color-scheme:light]: the GIS iframe is a light-scheme document; inside a dark-scheme
            // element (`.dark` sets color-scheme: dark) the browser paints it an opaque white
            // backdrop instead of keeping it transparent — the white box seen on hosted envs.
            <div ref={googleWrapRef} className="flex justify-center mb-1.5 [color-scheme:light]" style={{ minHeight: 44 }}>
              <GoogleLogin
                onSuccess={(cr) => { if (cr.credential) handleGoogleSuccess(cr.credential); }}
                onError={() => setError(t('auth.googleFailed', 'Google sign-in failed.'))}
                width={String(googleWidth)}
                theme={isDark ? 'filled_black' : 'outline'}
                shape="pill"
              />
            </div>
          )}

          {showGoogle && showNative && (
            <div className="flex items-center my-5">
              <hr className="flex-1 border-ui-border" />
              <span className="px-4 text-ui-text3 text-xs font-medium tracking-wider">OR</span>
              <hr className="flex-1 border-ui-border" />
            </div>
          )}

          {showNative && (
            <form onSubmit={handleNativeLogin} className="space-y-4">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-ui-text mb-1.5">
                  {t('auth.email')}
                </label>
                <div className="relative">
                  <input
                    id="login-email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className={fieldClass()}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-ui-text mb-1.5">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <input
                    id="login-password"
                    ref={passwordRef}
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className={cn(fieldClass(), 'pr-12')}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
                      if (!showPassword) {
                        setShowPassword(true);
                        hideTimerRef.current = setTimeout(() => setShowPassword(false), 2000);
                      } else {
                        setShowPassword(false);
                      }
                    }}
                    className="absolute inset-y-0 right-4 flex items-center text-icon hover:text-ui-text"
                    tabIndex={-1}
                  >
                    <EyeIcon off={showPassword} />
                  </button>

                  <button
                    type="button"
                    onMouseDown={() => setShowPassword(true)}
                    onMouseUp={() => setShowPassword(false)}
                    onMouseLeave={() => setShowPassword(false)}
                    onTouchStart={() => setShowPassword(true)}
                    onTouchEnd={() => setShowPassword(false)}
                    className="absolute inset-y-0 right-4 flex items-center text-icon hover:text-ui-text"
                    tabIndex={-1}
                    aria-label="Hold to show password"
                  >
                    {showPassword ? (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
                      </svg>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {error && <p role="alert" className="text-danger-text text-sm">{error}</p>}

              <Button type="submit" size="lg" className="w-full" loading={isLoading}>
                {isLoading ? t('common.loading') : 'Sign in'}
              </Button>

              <div className="flex justify-between text-sm pt-1">
                <span className="text-ui-text2">
                  New company owner?{' '}
                  <button type="button" onClick={() => navigate('/register')} className="text-accent-text hover:underline font-medium">
                    Register your company
                  </button>
                </span>
                <button type="button" onClick={() => navigate('/forgot-password')} className="text-accent-text hover:underline">
                  Forgot password?
                </button>
              </div>
            </form>
          )}

          {error && !showNative && <p role="alert" className="text-danger-text text-sm mt-3">{error}</p>}

          {!showNative && (
            <div className="mt-6 flex justify-between text-sm">
              <span className="text-ui-text2">
                New company owner?{' '}
                <button onClick={() => navigate('/register')} className="text-accent-text hover:underline font-medium">
                  Register your company
                </button>
              </span>
              <button onClick={() => navigate('/forgot-password')} className="text-accent-text hover:underline">
                Forgot password?
              </button>
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
